import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ActionList,
  Box,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  EmptyState,
  Highlight,
  Icon,
  RowsSkeletonLoader,
  Switch,
  toast,
} from '@node-space/ui-components'
import { logSentryError } from '@node-space/utils'
import { Lottie } from 'components/Animations/SimpleLottie'
import { Approval, DurationInMilliseconds } from 'constants/General'
import { useManageApproversMutation } from 'hooks/mutations/useManageApproversMutation'
import { AccountUser } from 'types/accounts'
import { BaseErrorResponse } from 'types/beneficiaries'
import { UserGroups } from '../const'

interface TeamMemberTableProps {
  accountUsers: AccountUser[]
  currentEmailAddress: string
  onEditUserClick: (accountUser: AccountUser) => void
  onDeleteUserClick: (accountUser: AccountUser) => void
  invalidateAccountUsers?: () => void
  isFetchingAccountUsers?: boolean
  defaultNumberOfRows?: number | string
  canEdit?: boolean
  canManageApprover?: boolean
}

export const TeamMemberTable = ({
  accountUsers,
  currentEmailAddress,
  onEditUserClick,
  onDeleteUserClick,
  invalidateAccountUsers,
  isFetchingAccountUsers,
  canEdit,
  canManageApprover,
  defaultNumberOfRows,
}: TeamMemberTableProps) => {
  const { t } = useTranslation()

  const [rowIndexIsLoading, setRowIndexIsLoading] = useState<number>()

  const { mutate: mutateManageApprovers, isPending: toggleApproverIsProcessing } =
    useManageApproversMutation()

  const toggleApprover = (rowIndex: number) => {
    setRowIndexIsLoading(rowIndex)
    const updatedTeamMemberValues = [...accountUsers]

    const toggleApproverPayload = [
      {
        emailAddress: updatedTeamMemberValues?.[rowIndex]?.emailAddress,
        approver: !updatedTeamMemberValues?.[rowIndex]?.approver,
      },
    ]

    mutateManageApprovers(
      { updateUserRoleGroups: toggleApproverPayload },
      {
        onSuccess: () => {
          toast({
            header: t('approvals.manageApproversSuccessToastMessage'),
            variant: 'success',
            duration: DurationInMilliseconds.TEN_SECONDS,
          })
          invalidateAccountUsers()
          setRowIndexIsLoading(null)
        },
        onError: (error: BaseErrorResponse) => {
          toast({
            header: t('oopsSomethingWentWrong'),
            variant: 'error',
            duration: DurationInMilliseconds.TEN_SECONDS,
          })
          setRowIndexIsLoading(null)
          logSentryError('Error from TeamMemberTable - toggleApprovers ', error)
        },
      }
    )
  }

  const accountUsersList = useMemo(() => {
    return accountUsers?.map?.((item, rowIndex) => {
      let groupList = 'N/A'
      if (item?.groups?.length) {
        groupList = item?.groups
          ?.filter(userGroup => userGroup?.name !== Approval.ACCOUNT_TRANSACTION_APPROVER)
          ?.map(group => {
            return group?.name
          })
          .join(', ')
      }

      const isWorkspaceOwner = groupList === UserGroups['Workspace Owner']

      return {
        key: item?.id,
        transparent: true,
        id: `wallet-row-${item?.id}`,
        title: item?.name || item?.emailAddress,
        testid: `team-member-${item?.id}`,
        BodyPre: <>{item?.emailAddress}</>,
        Switch: (
          <Switch
            disabled={
              !canManageApprover ||
              item?.emailAddress == currentEmailAddress ||
              item?.isWorkspaceOwner
            }
            loading={rowIndexIsLoading === rowIndex ? true : undefined}
            label={t('teamMember.approver')}
            value={item?.approver || item?.isWorkspaceOwner}
            onChange={() => toggleApprover(rowIndex)}
          />
        ),
        HeaderHighlightLeft: (
          <Box className="pr-2">
            <Highlight type={isWorkspaceOwner ? 'feature' : 'neutral'} variant="emphasis">
              {groupList}
            </Highlight>
          </Box>
        ),
        RightPrimaryText: '',
        CustomAction:
          item?.emailAddress !== currentEmailAddress && canEdit ? (
            <DropdownMenu
              DropdownMenuItems={[
                <DropdownMenuItem
                  key={`${item?.id}-edit`}
                  LeadingIcon={<Icon name="SimplePencilCustom" size="small" />}
                  onClick={() => onEditUserClick(item)}
                >
                  {t('teamMember.changeUserRole')}
                </DropdownMenuItem>,
                <DropdownMenuItem
                  key={`${item?.id}-details`}
                  LeadingIcon={<Icon name="Info" />}
                  onClick={() => onDeleteUserClick(item)}
                >
                  {t('delete')}
                </DropdownMenuItem>,
              ]}
            >
              <Button iconElement={<Icon name="DotsThreeVerticalCustom" />} variant="ghost" />
            </DropdownMenu>
          ) : (
            <Button
              disabled={true}
              iconElement={<Icon name="DotsThreeVerticalCustom" />}
              variant="ghost"
            />
          ),
      }
    })
  }, [accountUsers])

  return (
    <>
      {!accountUsersList && !isFetchingAccountUsers && (
        <EmptyState
          testId="empty-state"
          LottiePlayer={<Lottie src="no-data" />}
          title={t('noAccountUsersExist')}
        />
      )}

      {isFetchingAccountUsers && <RowsSkeletonLoader rows={Number(defaultNumberOfRows)} />}

      {!isFetchingAccountUsers && accountUsersList && (
        <ActionList id="team-members-action-list" items={accountUsersList || []} />
      )}
    </>
  )
}
